import { useCurrentMeeting } from "../contexts/currentMeeting";
import { createContext, useContext, useMemo, useState } from "react";
import { ContentType, GroupMessage } from "@/api";
import {
    MessagesSubscription,
    MessagesDocument,
    MessagesSubscriptionVariables,
    SendMessageMutation,
    SendMessageDocument,
    SendMessageMutationVariables,
    UpdateMessageMutation,
    UpdateMessageDocument,
    UpdateMessageMutationVariables,
} from "./queries-chatto.generated";
import { Message } from "./types";
import { useChattoHttpMutation, useChattoWsSubscription } from "conference/apollo-client/chatto/hooks";
import { createLocalMomentFromUTC, createUtcMoment } from "conference/tools";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { createContextProvider } from "./widgets";

type SubscriptionMessage = MessagesSubscription["message"];

export const useChatMessagesFactory = () => {
    const [sendMessageToChatto] = useChattoHttpMutation<SendMessageMutation, SendMessageMutationVariables>(
        SendMessageDocument
    );
    const [updateChattoMessage] = useChattoHttpMutation<UpdateMessageMutation, UpdateMessageMutationVariables>(
        UpdateMessageDocument
    );
    const currentMeeting = useCurrentMeeting();
    const [messages, setMessages] = useState<Message[]>([]);
    const messageIds = useMemo<Set<string>>(() => new Set(), []);

    const user = currentMeeting.selfUri;
    const roomId = currentMeeting.chatRoomId;

    const updateMessages = (newMessages: SubscriptionMessage[]) => {
        const msgs = [...messages];

        for (const msg of newMessages) {
            if (messageIds.has(msg.messageId))
                msgs.splice(
                    msgs.findIndex((m) => m.messageId === msg.messageId),
                    1
                );
            else messageIds.add(msg.messageId);

            msgs.push({
                ...msg,
                date: createLocalMomentFromUTC(createUtcMoment(msg.date)),
                isLocal: false,
            });
        }

        setMessages(msgs);
    };

    useChattoWsSubscription<MessagesSubscription, MessagesSubscriptionVariables>(MessagesDocument, {
        variables: { user, roomId },
        onData: (options) => {
            const msg = options.data.data.message;
            updateMessages([msg]);
        },
    });

    const sendMessage = async (content: string, contentType: ContentType = ContentType.PlainText): Promise<string> => {
        const messageId = uuidv4();

        const groupMessage: GroupMessage = {
            messageId,
            content,
            contentType,
            roomId,
            sender: user,
        };

        const preMessage: Message = {
            ...groupMessage,
            messageId, // well, some typescript shit...
            date: moment(),
            isLocal: true,
        };

        messageIds.add(messageId);
        setMessages([...messages, preMessage]);

        await sendMessageToChatto({
            variables: {
                groupMessage,
            },
        });

        return messageId;
    };

    const updateMessage = async (messageId: string, content: string) => {
        await updateChattoMessage({
            variables: {
                content,
                messageId,
                user,
            },
        });
    };

    return {
        messages,
        sendMessage,
        updateMessage,
    };
};

const ChatMessagesContext = createContext(undefined);

export const useChatMessages = (): ReturnType<typeof useChatMessagesFactory> => useContext(ChatMessagesContext);

export const ChatMessagesContextProvider = createContextProvider(ChatMessagesContext, useChatMessagesFactory);
